import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { CheckCircle2, XCircle, Download, Wifi, Gauge, Save } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Switch } from "components/ui/switch";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";
import api from "lib/api";

const SELECT_CLASS =
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

function fmtSecs(s) {
  if (s == null) return "—";
  if (s < 60) return `${s < 10 ? s.toFixed(1) : Math.round(s)} s`;
  const m = s / 60;
  if (m < 60) return `${m.toFixed(1)} min`;
  return `${(m / 60).toFixed(1)} h`;
}

// Ollama pull goes: pulling manifest → downloading (bytes %) → verifying digest →
// writing manifest → success. Only the download phase has a real %.
function pullPhase(pull) {
  const s = (pull?.status || "").toLowerCase();
  if (s.includes("success")) return "done";
  if (s.includes("verif") || s.includes("writing") || s.includes("manifest") || s.includes("install")) return "installing";
  return "downloading";
}
function pullLabel(pull, t) {
  const phase = pullPhase(pull);
  if (phase === "installing") return t("ai.installing", "installing…");
  if (phase === "done") return t("ai.pullOk", "done");
  return `${t("ai.downloading", "downloading")} ${pull.pct || 0}%`;
}

function StepHeader({ n, title, icon: Icon, desc }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <span className="flex items-center justify-center h-7 w-7 rounded-full bg-primary/10 text-primary text-sm font-semibold shrink-0">{n}</span>
      {Icon && <Icon className="h-5 w-5 text-muted-foreground" />}
      <div>
        <h2 className="font-semibold">{title}</h2>
        {desc && <p className="text-xs text-muted-foreground">{desc}</p>}
      </div>
    </div>
  );
}

export default function AI() {
  const { user } = useAuth();
  const { t } = useI18n();
  const isSuperAdmin = !!user?.capabilities?.manage_system;

  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState("");
  const [form, setForm] = useState({ ollama_url: "", ollama_vision_model: "", ollama_enabled: true });
  const [loading, setLoading] = useState(true);
  const [savingUrl, setSavingUrl] = useState(false);
  const [savingModel, setSavingModel] = useState(false);
  const [msg, setMsg] = useState("");

  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState(null);
  const [pulls, setPulls] = useState({});
  const [benching, setBenching] = useState(false);
  const [bench, setBench] = useState(null);
  const [probes, setProbes] = useState({});   // {model: {loading, usable, error}}
  const pollRef = useRef({});

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get("/api/companies/").then((r) => {
      setCompanies(r.data || []);
      if (r.data?.length) setCompany((c) => c || r.data[0].id);
    }).catch(() => {});
  }, [isSuperAdmin]);

  const load = useCallback((cid) => {
    setLoading(true);
    const q = cid ? `?company=${cid}` : "";
    api.get(`/api/ai-settings/${q}`)
      .then((r) => setForm({
        ollama_url: r.data.ollama_url || "",
        ollama_vision_model: r.data.ollama_vision_model || "",
        ollama_enabled: r.data.ollama_enabled,
      }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (isSuperAdmin) { if (company) load(company); } else load();
    setTest(null); setBench(null);
  }, [company, isSuperAdmin, load]);

  useEffect(() => () => { Object.values(pollRef.current).forEach(clearInterval); }, []);

  if (user && !user.capabilities?.manage_system) return <Navigate to="/liveness/admin/client-apps" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const payloadBase = () => (isSuperAdmin ? { company } : {});

  const saveUrl = async () => {
    setSavingUrl(true); setMsg("");
    try {
      await api.put("/api/ai-settings/", { ...payloadBase(), ollama_url: form.ollama_url, ollama_enabled: form.ollama_enabled });
      setMsg(t("common.saved", "Saved"));
    } catch { setMsg(t("recording.saveError", "Could not save")); }
    finally { setSavingUrl(false); }
  };

  const saveModel = async () => {
    setSavingModel(true); setMsg("");
    try {
      await api.put("/api/ai-settings/", { ...payloadBase(), ollama_vision_model: form.ollama_vision_model });
      setMsg(t("ai.modelSaved", "Model selected and saved"));
    } catch { setMsg(t("recording.saveError", "Could not save")); }
    finally { setSavingModel(false); }
  };

  const probeModel = async (model) => {
    setProbes((p) => ({ ...p, [model]: { loading: true } }));
    try {
      const r = await api.post("/api/ai-settings/probe/", { ollama_url: form.ollama_url, model });
      setProbes((p) => ({ ...p, [model]: r.data }));
    } catch (e) {
      setProbes((p) => ({ ...p, [model]: { usable: false, error: e.response?.data?.error || e.message } }));
    }
  };

  const runTest = async () => {
    setTesting(true); setBench(null); setProbes({});
    try {
      const r = await api.post("/api/ai-settings/test/", { ollama_url: form.ollama_url, ollama_vision_model: form.ollama_vision_model });
      setTest(r.data);
      // Light usability probe on each installed recommended model (sequential) so
      // an unusable one (e.g. mllama arch) is flagged and skipped in the benchmark.
      const toProbe = (r.data.recommended || []).filter((m) => m.installed).map((m) => m.name);
      (async () => { for (const m of toProbe) await probeModel(m); })();
    } catch (e) {
      setTest({ ok: false, error: e.response?.data?.error || e.message });
    } finally { setTesting(false); }
  };

  const startPull = async (model) => {
    setPulls((p) => ({ ...p, [model]: { pct: 0, status: "starting", done: false } }));
    try {
      await api.post("/api/ai-settings/pull/", { ollama_url: form.ollama_url, model });
      pollRef.current[model] = setInterval(async () => {
        try {
          const r = await api.get(`/api/ai-settings/pull-status/?model=${encodeURIComponent(model)}`);
          setPulls((p) => ({ ...p, [model]: r.data }));
          if (r.data.done) {
            clearInterval(pollRef.current[model]);
            delete pollRef.current[model];
            runTest();
          }
        } catch { /* keep polling */ }
      }, 1500);
    } catch (e) {
      setPulls((p) => ({ ...p, [model]: { pct: 0, status: "error", done: true, error: e.message } }));
    }
  };

  const runBench = async () => {
    const installed = (test?.models || []);
    const rec = (test?.recommended || []).filter((m) => m.installed).map((m) => m.name);
    const models = (rec.length ? rec : installed).slice(0, 6).filter((m) => probes[m]?.usable !== false);
    if (!models.length) return;
    setBenching(true);
    // Show the table immediately with a row per model; fill each in as it responds.
    setBench({ assumption: "~1 validation call per 120s segment", results: models.map((m) => ({ model: m, pending: true })) });
    const patch = (m, data) => setBench((b) => b && ({ ...b, results: b.results.map((x) => (x.model === m ? { model: m, ...data } : x)) }));
    for (const m of models) {
      try {
        const r = await api.post("/api/ai-settings/benchmark/", { ollama_url: form.ollama_url, models: [m] });
        patch(m, r.data.results?.[0] || { error: "no result" });
      } catch (e) {
        patch(m, { error: e.response?.data?.error || e.message });
      }
    }
    setBenching(false);
  };

  return (
    <div className="animate-fade-in">
      <h1 className="text-2xl font-bold tracking-tight mb-1">{t("ai.title", "AI")}</h1>
      <p className="text-sm text-muted-foreground mb-6">{t("ai.subtitle", "Ollama server for detection refinement")}</p>

      <Card className="w-full">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" /></div>
          ) : (
            <div className="divide-y divide-border">
              {isSuperAdmin && (
                <div className="pb-6">
                  <Label htmlFor="ai-company">{t("common.company", "Company")}</Label>
                  <select id="ai-company" value={company} onChange={(e) => setCompany(e.target.value)} className={`${SELECT_CLASS} mt-1.5`}>
                    {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              )}

              {/* 1 — URL */}
              <div className="py-6 first:pt-0">
                <StepHeader n={1} icon={Save} title={t("ai.step1", "Ollama server URL")} />
                <div className="space-y-3">
                  <Input value={form.ollama_url} onChange={(e) => set("ollama_url", e.target.value)} placeholder="http://ollama:11434" />
                  {form.ollama_vision_model && (
                    <div className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                      {t("ai.currentModel", "Selected model")}: <span className="font-mono text-foreground">{form.ollama_vision_model}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-2">
                      <Switch checked={form.ollama_enabled} onChange={(v) => set("ollama_enabled", v)} ariaLabel={t("ai.enabled", "Ollama refinement")} />
                      <span className="text-sm">{t("ai.enabled", "Ollama refinement")}</span>
                    </div>
                    <Button onClick={saveUrl} loading={savingUrl}><Save className="h-4 w-4 mr-1.5" />{t("common.save", "Save")}</Button>
                  </div>
                </div>
              </div>

              {/* 2 — Test connectivity */}
              <div className="py-6">
                <StepHeader n={2} icon={Wifi} title={t("ai.step2", "Test connectivity")} />
                <div className="flex items-center gap-3 flex-wrap">
                  <Button variant="outline" onClick={runTest} loading={testing} disabled={!form.ollama_url}>
                    <Wifi className="h-4 w-4 mr-1.5" />{t("ai.testConnectivity", "Test connectivity")}
                  </Button>
                  {test && (test.ok
                    ? <span className="inline-flex items-center gap-1.5 text-green-600 text-sm"><CheckCircle2 className="h-4 w-4" />{t("ai.testOk", "Connection OK")} · {test.models?.length || 0} {t("ai.modelsFound", "models")}{test.version ? ` · Ollama v${test.version}` : ""}</span>
                    : <span className="inline-flex items-center gap-1.5 text-red-500 text-sm"><XCircle className="h-4 w-4" />{test.error}</span>
                  )}
                </div>
              </div>

              {/* 3 — Models table + download */}
              {test?.ok && (
                <div className="py-6">
                  <StepHeader n={3} icon={Download} title={t("ai.step3", "Available & recommended models")} />
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground">
                          <th className="text-left py-2 font-medium">{t("ai.model", "Model")}</th>
                          <th className="text-left py-2 font-medium">{t("ai.status", "Status")}</th>
                          <th className="text-right py-2 font-medium">{t("common.actions", "Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(test.recommended || []).map((m) => {
                          const pull = pulls[m.name];
                          return (
                            <tr key={m.name} className="border-b border-border last:border-0">
                              <td className="py-2"><span className="font-mono text-xs">{m.name}</span><span className="text-xs text-muted-foreground ml-2">{m.label}</span></td>
                              <td className="py-2">
                                {m.installed ? (
                                  probes[m.name]?.loading ? (
                                    <span className="inline-flex items-center gap-1.5 text-muted-foreground text-xs"><span className="h-3 w-3 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />{t("ai.checking", "checking…")}</span>
                                  ) : probes[m.name]?.usable === false ? (
                                    <span className="inline-flex items-center gap-1 text-red-500 text-xs" title={probes[m.name]?.error || ""}><XCircle className="h-3.5 w-3.5" />{t("ai.unusable", "unusable")}</span>
                                  ) : (
                                    <span className="inline-flex items-center gap-1 text-green-600 text-xs"><CheckCircle2 className="h-3.5 w-3.5" />{probes[m.name]?.usable ? t("ai.usable", "usable") : t("ai.installed", "installed")}</span>
                                  )
                                ) : pull && !pull.done ? (
                                  <span className="inline-flex items-center gap-1.5 text-blue-500 text-xs">
                                    <span className="h-3 w-3 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
                                    {pullPhase(pull) === "installing" ? t("ai.installing", "installing…") : t("ai.downloading", "downloading")}
                                  </span>
                                ) : (
                                  <span className="text-amber-600 text-xs">{t("ai.notInstalled", "not installed")}</span>
                                )}
                                {m.installed && probes[m.name]?.usable === false && probes[m.name]?.error && (
                                  <div className="text-[11px] text-red-500/80 mt-0.5 max-w-xs truncate" title={probes[m.name].error}>{probes[m.name].error}</div>
                                )}
                              </td>
                              <td className="py-2 text-right">
                                {m.installed ? null : pull && !pull.done ? (
                                  <div className="w-44 ml-auto">
                                    <div className="h-2 rounded-full bg-muted overflow-hidden">
                                      <div
                                        className={`h-full bg-primary transition-all ${pullPhase(pull) === "installing" ? "animate-pulse" : ""}`}
                                        style={{ width: `${pullPhase(pull) === "downloading" ? (pull.pct || 0) : 100}%` }}
                                      />
                                    </div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5 truncate">{pullLabel(pull, t)}</div>
                                  </div>
                                ) : (
                                  <Button variant="outline" onClick={() => startPull(m.name)}>
                                    <Download className="h-3.5 w-3.5 mr-1" />{t("ai.download", "Download")}
                                  </Button>
                                )}
                                {pull?.done && pull.error && <span className="text-[11px] text-red-500">{pull.error}</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {test.models?.length ? (
                    <div className="mt-3 text-xs text-muted-foreground">
                      <span className="font-medium">{t("ai.allInstalled", "Installed on server")}:</span> {test.models.join(", ")}
                    </div>
                  ) : null}
                </div>
              )}

              {/* 4 — Benchmark */}
              {test?.ok && (
                <div className="py-6">
                  <StepHeader n={4} icon={Gauge} title={t("ai.step4", "Benchmark models")} desc={bench?.assumption} />
                  <Button variant="outline" onClick={runBench} loading={benching}><Gauge className="h-4 w-4 mr-1.5" />{t("ai.runBenchmark", "Run benchmark")}</Button>
                  {bench?.results?.length ? (
                    <div className="overflow-x-auto mt-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-border text-muted-foreground">
                            <th className="text-left py-2 font-medium">{t("ai.model", "Model")}</th>
                            <th className="text-right py-2 font-medium">ms/{t("ai.call", "call")}</th>
                            <th className="text-right py-2 font-medium">{t("ai.tokens", "tokens (in/out)")}</th>
                            <th className="text-right py-2 font-medium">{t("ai.per120", "120s segment")}</th>
                            <th className="text-right py-2 font-medium">{t("ai.exam2h", "2h exam")}</th>
                            <th className="text-right py-2 font-medium">{t("ai.exam8h", "8h exam")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bench.results.map((r) => (
                            <tr key={r.model} className="border-b border-border last:border-0">
                              <td className="py-2 font-mono text-xs">{r.model}</td>
                              {r.pending
                                ? <td colSpan={5} className="py-2 text-muted-foreground text-xs">
                                    <span className="inline-flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border-2 border-muted-foreground border-t-transparent animate-spin" />{t("ai.testing", "testing…")}</span>
                                  </td>
                                : r.error
                                ? <td colSpan={5} className="py-2 text-red-500 text-xs">{r.error}</td>
                                : <>
                                    <td className="py-2 text-right tabular-nums">{r.ms_per_call}</td>
                                    <td className="py-2 text-right tabular-nums">{r.prompt_tokens != null ? `${r.prompt_tokens} / ${r.eval_tokens ?? 0}` : "—"}</td>
                                    <td className="py-2 text-right tabular-nums">{fmtSecs(r.seg120_s)}</td>
                                    <td className="py-2 text-right tabular-nums">{fmtSecs(r.exam2h_s)}</td>
                                    <td className="py-2 text-right tabular-nums">{fmtSecs(r.exam8h_s)}</td>
                                  </>}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              )}

              {/* 5 — Select model */}
              {test?.ok && (
                <div className="py-6 last:pb-0">
                  <StepHeader n={5} icon={CheckCircle2} title={t("ai.step5", "Select the model to use")} />
                  <div className="space-y-3">
                    <select value={form.ollama_vision_model} onChange={(e) => set("ollama_vision_model", e.target.value)} className={SELECT_CLASS}>
                      <option value="">{t("ai.selectModel", "Select a model…")}</option>
                      {(test.models || []).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                    <div className="flex items-center justify-between">
                      {msg ? <span className="text-sm text-green-600">{msg}</span> : <span />}
                      <Button onClick={saveModel} loading={savingModel} disabled={!form.ollama_vision_model}>
                        <Save className="h-4 w-4 mr-1.5" />{t("ai.useModel", "Use this model")}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
